import * as echarts from 'echarts';
import { useCallback, useEffect, useRef, useState } from "react";
import { IResponseSummarySites } from "../../../util/services/site";
import { fishHealth } from '../../../util/varibles/constants';
import { removeProperty } from '../../../util/varibles/global';
import { FILTER_ID } from '../Filter';
import { setSiteOverview } from '../SiteOverviewContext';
import styles from "./style.module.scss";

interface IProps {
    data: IResponseSummarySites
}

const FishHealth: React.FC<IProps> = ({ data }) => {
    const chartRef = useRef<any>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [view, setView] = useState<{ name: string, description: string, value: number, key: string, color: string, bg: string }[]>([]);
    const { unit_health_map } = data;
    const set = setSiteOverview();
    const countUnits = Object.keys(data.units || {}).length;

    const handleChange = useCallback((key: string, oldFilter: typeof unit_health_map) => {
        setSelected(prev => {
            const temp = new Set(Array.from(prev));
            if (temp.has(key)) {
                temp.delete(key);
            } else {
                temp.add(key);
            }
            const ids = new Set(Array.from(temp).reduce((rs: string[], id) => [...rs, ...oldFilter?.[id] || []], []));
            if (!ids.size) {
                set(({ unitsFilter: prev }) => ({ unitsFilter: removeProperty(prev, FILTER_ID.FISH_HEALTH) }))
            } else {
                set(({ unitsFilter: prev }) => ({ unitsFilter: { ...prev, [FILTER_ID.FISH_HEALTH]: ({ id }) => ids.has(id || "") } }))
            }
            return temp
        });
    }, [set])

    useEffect(() => {
        const content: typeof view = [];
        Object.keys(fishHealth).forEach((key) => {
            const item = fishHealth[key];
            const count = unit_health_map?.[item.id]?.length || 0;
            if (!count) return null;
            content.push({
                name: item.name,
                description: `${countUnits ? ` ${Math.round(count / countUnits * 10000) / 100}%` : ""} ${item.name}`,
                value: count,
                key: key,
                color: item.color,
                bg: item.bg,
            })
        });
        const { null: none } = unit_health_map || {};
        if (none && none.length) {
            const name = "No status";
            content.push({
                name,
                description: `${countUnits ? ` ${Math.round(none.length / countUnits * 10000) / 100}%` : ""} ${name}`,
                value: none.length,
                key: "none",
                color: "#FFF",
                bg: "#ABA89C"
            })
        }
        setView(content);
        if (chartRef.current) {
            const existInstance = echarts.getInstanceByDom(chartRef.current);
            if (existInstance) {
                echarts.dispose(chartRef.current)
            };
            const chart = echarts.init(chartRef.current);
            const option = {
                tooltip: {
                    trigger: 'item'
                },

                series: [
                    {
                        name: 'Fish Health',
                        type: 'pie',
                        label: {
                            show: false
                        },
                        labelLine: { show: false },
                        radius: ['0%', '80%'],
                        center: ['50%', '50%'],
                        data: content.map(item => ({
                            value: item.value, name: item.name, itemStyle: { color: item.bg }
                        })),
                        itemStyle: {
                            borderWidth: 2,
                            borderColor: '#fff'
                        }
                    }
                ]
            };
            chart.setOption(option);
            chart.on('click', 'series.pie', (params) => {
                const { dataIndex } = params;
                const { key } = content[dataIndex] || {};
                if (key) {
                    handleChange(key, unit_health_map)

                }
            })
        }
    }, [countUnits, handleChange, unit_health_map]);

    return <div className={styles.section}>
        <div className={styles.headerSection}>
            Fish Health
        </div>
        <div className={styles.bodySection}>
            <div ref={chartRef} className='w-full h-[100px] mt-[6px]' />
            <div className='flex flex-wrap justify-center px-1'>
                {view.map((item, i) => {
                    return <span
                        key={item.key}
                        className={styles.fishHealth}
                        data-selected={selected.has(item.key)}
                        style={{ "--color-active": item.color, "--bg-active": item.bg } as any}
                        onClick={() => handleChange(item.key, unit_health_map)}
                    >
                        {item.description}
                    </span>
                })}
            </div>
        </div>
    </div>
}

export default FishHealth
